import React, { useEffect, useId, useRef } from 'react'
import {
  Columns,
  Icon,
  SegControl,
  Stack,
  Text,
} from '@lightspeed/design-system-react'
import { T } from '@transifex/react'
import { FulfilmentType } from '../../../../../../../models/fulfilment'

interface FulfilmentSelectLegacyProps {
  title?: string
  onTypeChange: (type: FulfilmentType) => void
  defaultType?: string
  showUnspecified?: boolean
  message?: string
  disabled?: boolean
  stretched?: boolean
  outOfStockMessage?: string
  name?: string
}

export const FulfilmentSelectLegacy: React.FC<FulfilmentSelectLegacyProps> = ({
  title,
  defaultType,
  onTypeChange,
  showUnspecified,
  message,
  name,
  disabled,
  stretched,
  outOfStockMessage,
}) => {
  const fallbackFulfilmentTypeName = `fulfilType-${useId()}`
  const fulfilmentTypeName = name || fallbackFulfilmentTypeName
  const unspecifiedRef = useRef<HTMLLabelElement>(null)
  const deliveryRef = useRef<HTMLLabelElement>(null)
  const pickupRef = useRef<HTMLLabelElement>(null)

  useEffect(() => {
    // Add data-track to the label element for proper tracking
    if (unspecifiedRef.current) {
      unspecifiedRef.current.setAttribute(
        'data-track',
        'unspecified-fulfilment-label'
      )
    }
    if (deliveryRef.current) {
      deliveryRef.current.setAttribute(
        'data-track',
        'delivery-fulfilment-label'
      )
    }
    if (pickupRef.current) {
      pickupRef.current.setAttribute('data-track', 'pickup-fulfilment-label')
    }
  }, [])

  return (
    <Stack space={2}>
      {outOfStockMessage && (
        <Text type="supplementary" className="vd-mb4">
          {outOfStockMessage}
        </Text>
      )}
      <Text type="label">
        {title || <T _str="Fulfillment method" _context="Fulfilment select" />}
      </Text>
      <div
        className="vd-flex"
        onClick={event => {
          // Prevent closing the Line Item Fulfilment Popover
          event.stopPropagation()
        }}
      >
        {showUnspecified && (
          <SegControl
            ref={unspecifiedRef}
            name={fulfilmentTypeName}
            role="radio"
            stretched={stretched}
            disabled={defaultType !== FulfilmentType.UNSPECIFIED && disabled}
            checked={defaultType === FulfilmentType.UNSPECIFIED}
            onChange={() => onTypeChange(FulfilmentType.UNSPECIFIED)}
            data-track="unspecified-fulfilment"
          >
            <T _str="None" _context="Fulfilment select" />
          </SegControl>
        )}
        <SegControl
          ref={deliveryRef}
          name={fulfilmentTypeName}
          role="radio"
          stretched={stretched}
          disabled={defaultType !== FulfilmentType.DELIVERY && disabled}
          checked={defaultType === FulfilmentType.DELIVERY}
          onChange={() => onTypeChange(FulfilmentType.DELIVERY)}
          data-track="delivery-fulfilment"
        >
          <T _str="Delivery" _context="Fulfilment select" />
        </SegControl>
        <SegControl
          ref={pickupRef}
          name={fulfilmentTypeName}
          role="radio"
          stretched={stretched}
          disabled={defaultType !== FulfilmentType.PICKUP && disabled}
          checked={defaultType === FulfilmentType.PICKUP}
          onChange={() => onTypeChange(FulfilmentType.PICKUP)}
          data-track="pickup-fulfilment"
        >
          <T _str="Pickup" _context="Fulfilment select" />
        </SegControl>
      </div>
      {message && (
        <Columns space={2}>
          <Text type="supplementary">
            <Icon icon="fa-info-circle" />
          </Text>
          <Text role="textbox" type="supplementary">
            {message}
          </Text>
        </Columns>
      )}
    </Stack>
  )
}

FulfilmentSelectLegacy.displayName = 'FulfilmentSelectLegacy'
